import { useCallback, useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, Send, ShieldCheck, AlertTriangle, CreditCard } from "lucide-react";
import { api, describeApiError } from "../lib/config";
import { useInView } from "../lib/useInView";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * iter197 — Admin AutoPay enrollment control for existing recurring
 * customers. Reads /api/admin/customers/{user_id}/autopay-status and
 * exposes:
 *   • Enable AutoPay      → POST /enable-autopay
 *                             ↳ verifies the saved PM is reusable
 *                                off-session via Stripe SetupIntent.
 *                                Instant activation if reusable.
 *   • Send Authorization  → POST /send-autopay-authorization
 *                             ↳ mails + SMS a one-tap link so the
 *                                customer can approve a fresh SetupIntent
 *                                Checkout. Never assumes prior invoice
 *                                payment implies off-session consent.
 *   • Disable AutoPay     → PUT /admin/customers/{user_id}/autopay
 *
 * The `compact` prop renders the pill-only view (used in the booking
 * card grid). The default expanded view shows status + one primary
 * action + optional secondary action.
 */

const STATUS_PILL = {
  ON:                     { label: "AutoPay ON",           classes: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  OFF:                    { label: "AutoPay OFF",          classes: "bg-gray-100 text-gray-700 border-gray-200" },
  AUTHORIZATION_REQUIRED: { label: "Auth Required",        classes: "bg-amber-50 text-amber-900 border-amber-300" },
  PAYMENT_PENDING:        { label: "Payment Pending",      classes: "bg-amber-50 text-amber-900 border-amber-300" },
  PAYMENT_FAILED:         { label: "AutoPay Failed",       classes: "bg-rose-50 text-rose-800 border-rose-200" },
  PAID:                   { label: "AutoPay ON — Paid",    classes: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export function AutoPayStatusPill({ status, testId }) {
  const meta = STATUS_PILL[status] || STATUS_PILL.OFF;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${meta.classes}`}
      data-testid={testId || "autopay-status-pill"}
    >
      <ShieldCheck className="w-3 h-3" /> {meta.label}
    </span>
  );
}

export default function AutoPayEnrollmentControl({
  userId,
  compact = false,
  onChange,
  testIdPrefix = "autopay-enrollment",
}) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // iter210 — Rescue AutoPay: scan Stripe by email for a saved PM
  // that was never persisted onto the JDSN user record (e.g. pre-iter202
  // invoice payments where the PM ended up on Stripe Link only).
  const [rescueBusy, setRescueBusy] = useState(false);
  const [inViewRef, visible] = useInView();

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await api.get(`/admin/customers/${userId}/autopay-status`);
      setState(data);
    } catch (e) {
      // silent — admin will see nothing rather than a noisy toast
    }
  }, [userId]);

  useEffect(() => { if (visible) load(); }, [load, visible]);

  const handleEnable = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      // iter211 — If the JDSN user record has no PM yet, auto-fire the
      // Stripe rescue scan (iter210) BEHIND THE SCENES before opening
      // the confirmation dialog. Owner directive 2026-03-01: one-click
      // Enable AutoPay must work even for pre-iter202 legacy customers
      // whose PM lives in Stripe but was never persisted to the JDSN
      // user record.
      if (!hasPm) {
        try {
          const { data: r } = await api.post(`/admin/customers/${userId}/rescue-autopay`, {});
          if (!r.rescued && r.reason !== "already_has_pm") {
            if (r.reason === "no_matching_stripe_customer") {
              toast.error("No Stripe customer found for this email. Send them an authorization link to save a card first.");
            } else if (r.reason === "no_saved_card_in_stripe") {
              toast.error("Stripe customer exists but has no saved card. Send them an authorization link.");
            } else if (r.reason === "no_email_on_customer") {
              toast.error("Customer has no email on file — cannot look up Stripe.");
            } else {
              toast.error(`Cannot locate a saved card: ${r.reason || "unknown"}. Send an authorization link.`);
            }
            return;
          }
          // Refresh state so pm/hasPm reflect the newly rescued card
          await load();
        } catch (e) {
          toast.error(describeApiError(e, "Could not look up saved card"));
          return;
        }
      }
      // Now that we know a PM exists, the confirmation dialog will
      // show the correct card. `handleEnable` is invoked FROM the
      // dialog action button — but here we're the pre-dialog opener,
      // so open the dialog and stop.
      setConfirmOpen(true);
    } finally {
      setBusy(false);
    }
  };

  // iter207 — This runs when the admin confirms the dialog. Separated
  // from handleEnable so the dialog can call it directly on Confirm.
  const handleEnableConfirm = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/customers/${userId}/enable-autopay`, {
        admin_assisted: true,
        verify_stripe: false,
      });
      if (data.activated) {
        toast.success("AutoPay activated");
      } else if (data.status === "AUTHORIZATION_REQUIRED") {
        toast.info(data.hint || "Customer authorization required — send them the link");
      }
      await load();
      onChange?.(data);
    } catch (e) {
      toast.error(describeApiError(e, "Could not enable AutoPay"));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  const handleSendAuth = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/customers/${userId}/send-autopay-authorization`,
        { ttl_days: 7 },
      );
      const channels = Object.entries(data.sent || {})
        .filter(([, ok]) => ok)
        .map(([c]) => c)
        .join(" + ") || "no channels";
      toast.success(`Authorization link sent via ${channels}`);
      await load();
      onChange?.(data);
    } catch (e) {
      toast.error(describeApiError(e, "Could not send authorization link"));
    } finally {
      setBusy(false);
    }
  };

  // iter210 — Rescue AutoPay for legacy customers whose PM lives in
  // Stripe but was never persisted onto the JDSN user record.
  const handleRescue = async () => {
    if (!userId) return;
    setRescueBusy(true);
    try {
      const { data } = await api.post(`/admin/customers/${userId}/rescue-autopay`, {});
      if (data.rescued) {
        const pm = data.payment_method || {};
        toast.success(`Found saved card in Stripe: ${(pm.brand || "card").toUpperCase()} •••• ${pm.last4 || ""}. Click Enable AutoPay to activate.`);
      } else if (data.reason === "already_has_pm") {
        toast.info("Card on file already saved on this customer.");
      } else if (data.reason === "no_matching_stripe_customer") {
        toast.error("No Stripe customer found for this email. Ask the customer to pay an invoice or send them an authorization link.");
      } else if (data.reason === "no_saved_card_in_stripe") {
        toast.error("Stripe customer exists but has no saved card. Send them an authorization link.");
      } else if (data.reason === "no_email_on_customer") {
        toast.error("Customer has no email on file — cannot scan Stripe.");
      } else {
        toast.error(`Rescue failed: ${data.reason || "unknown"}`);
      }
      await load();
      onChange?.(data);
    } catch (e) {
      toast.error(describeApiError(e, "Could not scan Stripe"));
    } finally {
      setRescueBusy(false);
    }
  };


  const handleDisable = async () => {
    if (!userId) return;
    if (!window.confirm("Turn AutoPay OFF for this customer?")) return;
    setBusy(true);
    try {
      await api.put(`/admin/customers/${userId}/autopay`, { enabled: false });
      toast.success("AutoPay disabled");
      await load();
      onChange?.({ activated: false, status: "OFF" });
    } catch (e) {
      toast.error(describeApiError(e, "Could not disable AutoPay"));
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return compact ? <span ref={inViewRef} /> : (
      <div ref={inViewRef} className="flex items-center gap-2 text-[11px] text-gray-500">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading AutoPay status…
      </div>
    );
  }

  const status = state.autopay_status;
  const hasPm = state.has_saved_payment_method;
  const pm = state.payment_method;

  // Compact = pill only, for the customer card grid
  if (compact) {
    return (
      <AutoPayStatusPill status={status} testId={`${testIdPrefix}-pill-${userId}`} />
    );
  }

  const pmLabel = pm ? `${String(pm.brand || "card").toUpperCase()} •••• ${pm.last4 || "----"}` : null;

  // iter206 — Owner spec (2026-03-01). Three explicit visual states so
  // the admin can tell at a glance what's happening on ANY customer:
  //   ON + valid PM:  "AUTO PAY ON · Ready for automatic charges ·
  //                   Card on File: Visa •••• 4242 · Disable AutoPay"
  //   Has PM, not yet enabled/authorized:
  //                   "AUTO PAY OFF · Authorization Required ·
  //                   Card on File: Visa •••• 4242 ·
  //                   [Enable AutoPay] [Send Authorization]"
  //   No PM at all:   "AUTO PAY OFF · No Saved Card ·
  //                   [Send Authorization]"
  const isOn = status === "ON" || status === "PAID" ||
                status === "PAYMENT_PENDING" || status === "PAYMENT_FAILED";
  const needsAuth = !isOn && (status === "AUTHORIZATION_REQUIRED" ||
                                (status === "OFF" && hasPm));
  const noCard = !isOn && !hasPm;

  let subtitle;
  let subtitleClasses;
  if (isOn) {
    subtitle = "Ready for automatic charges";
    subtitleClasses = "text-emerald-800";
  } else if (needsAuth) {
    subtitle = "Authorization Required";
    subtitleClasses = "text-amber-900";
  } else {
    subtitle = "No Saved Card";
    subtitleClasses = "text-gray-600";
  }

  return (
    <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3"
         data-testid={`${testIdPrefix}-panel-${userId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <AutoPayStatusPill status={status} testId={`${testIdPrefix}-pill-${userId}`} />
        <span className={`text-[10px] font-extrabold uppercase tracking-[0.14em] ${subtitleClasses}`}
              data-testid={`${testIdPrefix}-subtitle-${userId}`}>
          {subtitle}
        </span>
      </div>

      {pmLabel && (
        <div className="mt-1.5 text-[11px] font-bold text-gray-700 flex items-center gap-1"
             data-testid={`${testIdPrefix}-pm-${userId}`}
             title="Saved payment method (PII-safe: brand + last 4 only)">
          <span className="uppercase tracking-[0.12em] text-[9px] text-gray-500">Card on File:</span>
          <span>{pmLabel}</span>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {/* iter211 — Enable AutoPay button is ALWAYS visible when
            AutoPay isn't already ON. On click it auto-fires the
            Stripe rescue scan (iter210) if no PM is stored, then
            opens the confirmation dialog. One-click for the admin. */}
        {!isOn && (
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy}
            data-testid={`${testIdPrefix}-enable-${userId}`}
            className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-[0.10em] px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Enable AutoPay
          </button>
        )}
        {(needsAuth || noCard) && (
          <button
            type="button"
            onClick={handleSendAuth}
            disabled={busy}
            data-testid={`${testIdPrefix}-send-auth-${userId}`}
            className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-[0.10em] px-3 py-1 rounded-full bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Send Authorization
          </button>
        )}
        {noCard && (
          <button
            type="button"
            onClick={handleRescue}
            disabled={rescueBusy}
            data-testid={`${testIdPrefix}-rescue-${userId}`}
            title="Scan Stripe by email to find a saved card from a prior invoice payment (legacy pre-iter202 rescue)"
            className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-[0.10em] px-3 py-1 rounded-full bg-white border border-sky-300 text-sky-800 hover:bg-sky-50 disabled:opacity-60"
          >
            {rescueBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
            Find Card in Stripe
          </button>
        )}
        {isOn && (
          <button
            type="button"
            onClick={handleDisable}
            disabled={busy}
            data-testid={`${testIdPrefix}-disable-${userId}`}
            className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-[0.10em] px-3 py-1 rounded-full bg-white border border-rose-300 text-rose-800 hover:bg-rose-50 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
            Disable AutoPay
          </button>
        )}
      </div>

      {status === "AUTHORIZATION_REQUIRED" && state.pending_authorization && (
        <div className="mt-2 text-[11px] text-amber-900 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Waiting on customer to approve the authorization link
          {state.authorization_link_expires_at ? ` · expires ${new Date(state.authorization_link_expires_at).toLocaleDateString()}` : ""}.
        </div>
      )}

      {/* iter207 — Admin-assisted enrollment confirmation. Owner spec
          (2026-03-01): before activating AutoPay from the admin side,
          show the saved card and the verbal-authorization confirmation
          wording. Cancel dismisses. Confirm hits the endpoint with
          admin_assisted=true (skips Stripe SetupIntent check, still
          ensures PM attached to the JDSN Stripe Customer). */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`${testIdPrefix}-confirm-dialog-${userId}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Enable AutoPay for this customer?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-1">
                {pmLabel && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center gap-2"
                       data-testid={`${testIdPrefix}-confirm-card-${userId}`}>
                    <CreditCard className="w-4 h-4 text-emerald-700" />
                    <div className="text-sm font-bold text-emerald-900">{pmLabel}</div>
                  </div>
                )}
                <div className="text-sm text-gray-700 leading-relaxed">
                  The customer has requested AutoPay using the existing
                  payment method ending in <b>{pm?.last4 || "----"}</b>.
                  Confirm to activate AutoPay for future recurring services.
                </div>
                <div className="text-[11px] text-gray-500 leading-snug">
                  This action is logged as an admin-assisted enrollment
                  with your verbal authorization from the customer. All
                  future unpaid recurring bookings will be updated to use
                  the saved card and charged automatically off-session on
                  service completion.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={busy}
              data-testid={`${testIdPrefix}-confirm-cancel-${userId}`}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleEnableConfirm(); }}
              disabled={busy}
              data-testid={`${testIdPrefix}-confirm-activate-${userId}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Confirm & Enable AutoPay
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
